import { useState, useCallback, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useStore } from "../store";
import { uploadScan } from "../api";
import type { UploadProgress, Environment } from "../types";

type ScanPhase = "idle" | "uploading" | "processing" | "complete" | "error";

export default function ScanUploader() {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fetchEnvironments = useStore((s) => s.fetchEnvironments);
  const environments = useStore((s) => s.environments);

  const [files, setFiles] = useState<File[]>([]);
  const [scanName, setScanName] = useState("");
  const [phase, setPhase] = useState<ScanPhase>("idle");
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [createdEnv, setCreatedEnv] = useState<Environment | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (projectId) {
      fetchEnvironments(projectId);
    }
  }, [projectId, fetchEnvironments]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const processFiles = useCallback((fileList: FileList | null) => {
    if (!fileList) return;
    const accepted = Array.from(fileList).filter((f) => {
      const ext = f.name.split(".").pop()?.toLowerCase() ?? "";
      return (
        ["mp4", "mov", "avi", "webm", "jpg", "jpeg", "png", "heic", "heif"].includes(ext)
      );
    });
    setFiles((prev) => [...prev, ...accepted]);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      processFiles(e.dataTransfer.files);
    },
    [processFiles],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      processFiles(e.target.files);
    },
    [processFiles],
  );

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpload = useCallback(async () => {
    if (!projectId || files.length === 0 || !scanName.trim()) return;

    setPhase("uploading");
    setErrorMsg(null);

    try {
      const env = await uploadScan(projectId, files, scanName.trim(), (p) => {
        setProgress(p);
      });

      setPhase("processing");
      setCreatedEnv(env);

      // Poll for completion (environment goes from "processing" -> "ready")
      const pollInterval = setInterval(async () => {
        try {
          const updated = await useStore
            .getState()
            .fetchEnvironment(projectId, env.id);
          if (updated.status === "ready") {
            clearInterval(pollInterval);
            setCreatedEnv(updated);
            setPhase("complete");
          } else if (updated.status === "failed") {
            clearInterval(pollInterval);
            setPhase("error");
            setErrorMsg("Environment processing failed. Please try again.");
          }
        } catch {
          // Keep polling on transient errors
        }
      }, 3000);

      // Safety timeout: stop polling after 10 minutes
      setTimeout(() => clearInterval(pollInterval), 600_000);
    } catch (e) {
      setPhase("error");
      setErrorMsg((e as Error).message);
    }
  }, [projectId, files, scanName]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Scan & Upload</h1>
          <p className="page-subtitle">
            Upload photos or video of your work cell to generate a 3D environment
          </p>
        </div>
      </div>

      {/* Upload area */}
      {phase === "idle" && (
        <>
          <div className="form-group">
            <label className="form-label" htmlFor="scan-name">
              Environment Name
            </label>
            <input
              id="scan-name"
              className="form-input"
              type="text"
              value={scanName}
              onChange={(e) => setScanName(e.target.value)}
              placeholder="e.g., Assembly Line B - North Side"
              style={{ maxWidth: 480 }}
            />
          </div>

          <div
            className={`dropzone${dragActive ? " active" : ""}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter") fileInputRef.current?.click();
            }}
          >
            <div className="dropzone-icon">{"\u21E7"}</div>
            <p className="dropzone-text">
              <strong>Drag & drop</strong> video or photos here, or{" "}
              <strong>click to browse</strong>
            </p>
            <p className="text-sm text-muted" style={{ marginTop: 8 }}>
              Supported: MP4, MOV, AVI, WebM, JPG, PNG, HEIC
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="video/*,image/*"
              onChange={handleFileInput}
              style={{ display: "none" }}
            />
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="mt-4">
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                Selected Files ({files.length})
              </h3>
              <div className="card">
                {files.map((file, i) => (
                  <div
                    key={`${file.name}-${i}`}
                    className="flex justify-between items-center"
                    style={{
                      padding: "8px 0",
                      borderBottom:
                        i < files.length - 1
                          ? "1px solid var(--color-border)"
                          : "none",
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: 500 }}>{file.name}</span>
                      <span className="text-sm text-muted" style={{ marginLeft: 12 }}>
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => removeFile(i)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 16 }}>
                <button
                  className="btn btn-primary btn-lg"
                  onClick={handleUpload}
                  disabled={!scanName.trim()}
                >
                  Upload & Generate Environment
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Upload progress */}
      {phase === "uploading" && progress && (
        <div className="card" style={{ maxWidth: 600 }}>
          <h3 className="card-title" style={{ marginBottom: 16 }}>
            Uploading...
          </h3>
          <div className="progress-bar" style={{ marginBottom: 8 }}>
            <div
              className="progress-bar-fill"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
          <p className="text-sm text-muted">
            {progress.percentage}% - {formatFileSize(progress.loaded)} /{" "}
            {formatFileSize(progress.total)}
          </p>
        </div>
      )}

      {/* Processing status */}
      {phase === "processing" && (
        <div className="card" style={{ maxWidth: 600 }}>
          <h3 className="card-title" style={{ marginBottom: 12 }}>
            Generating 3D Environment
          </h3>
          <p className="text-muted" style={{ marginBottom: 16 }}>
            RoboTwins is processing your scan into a 3D environment. This
            typically takes 2-5 minutes depending on complexity.
          </p>
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{
                width: "100%",
                animation: "pulse 2s ease-in-out infinite",
              }}
            />
          </div>
          <p className="text-sm text-muted" style={{ marginTop: 8 }}>
            Status: Processing scan data and generating world geometry...
          </p>
        </div>
      )}

      {/* Complete */}
      {phase === "complete" && createdEnv && (
        <div className="card" style={{ maxWidth: 600 }}>
          <h3 className="card-title" style={{ marginBottom: 12 }}>
            Environment Ready
          </h3>
          <p style={{ marginBottom: 16, color: "var(--color-success)" }}>
            Your 3D environment has been generated successfully.
          </p>
          <div className="flex gap-2">
            <button
              className="btn btn-primary"
              onClick={() =>
                navigate(
                  `/projects/${projectId}/environment/${createdEnv.id}`,
                )
              }
            >
              Open in 3D Viewer
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setPhase("idle");
                setFiles([]);
                setScanName("");
                setProgress(null);
                setCreatedEnv(null);
              }}
            >
              Upload Another
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {phase === "error" && (
        <div
          className="card"
          style={{ maxWidth: 600, borderColor: "var(--color-danger)" }}
        >
          <h3
            className="card-title"
            style={{ color: "var(--color-danger)", marginBottom: 12 }}
          >
            Upload Failed
          </h3>
          <p className="text-muted" style={{ marginBottom: 16 }}>
            {errorMsg ?? "An unexpected error occurred."}
          </p>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setPhase("idle");
              setErrorMsg(null);
              setProgress(null);
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Existing environments list */}
      {environments.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
            Existing Environments
          </h2>
          <div className="card-grid">
            {environments.map((env) => (
              <div
                key={env.id}
                className="card"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  navigate(
                    `/projects/${projectId}/environment/${env.id}`,
                  )
                }
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter")
                    navigate(
                      `/projects/${projectId}/environment/${env.id}`,
                    );
                }}
              >
                <div className="card-header">
                  <div className="card-title">{env.name}</div>
                  <span
                    className={`badge ${
                      env.status === "ready"
                        ? "badge-success"
                        : env.status === "processing"
                          ? "badge-warning"
                          : env.status === "failed"
                            ? "badge-danger"
                            : "badge-info"
                    }`}
                  >
                    {env.status}
                  </span>
                </div>
                <div className="card-meta">
                  {env.source_type === "video" ? "Video scan" : "Photo set"} -{" "}
                  {new Date(env.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
